import { useState, useEffect } from 'react'
import { XMarkIcon } from '@heroicons/react/24/outline'
import api from '@/services/api'

export default function StakingEditModal({ isOpen, onClose, stakingConfig, onUpdate }) {
  const [formData, setFormData] = useState({
    currency: 'krw',
    dailyAmount: '',
    description: '',
    isActive: true
  })
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    if (stakingConfig) {
      setFormData({
        currency: stakingConfig.currency || 'krw',
        dailyAmount: stakingConfig.dailyAmount || '',
        description: stakingConfig.description || '',
        isActive: stakingConfig.isActive !== undefined ? stakingConfig.isActive : true
      })
      setError('')
    }
  }, [stakingConfig])

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }))
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    
    if (!formData.dailyAmount || formData.dailyAmount <= 0) {
      setError('일일 지급량을 올바르게 입력해주세요.')
      return
    }

    setLoading(true)
    setError('')

    try {
      const updateData = {
        currency: formData.currency,
        dailyAmount: parseFloat(formData.dailyAmount),
        description: formData.description.trim(),
        isActive: formData.isActive
      }

      const response = await api.put(`/staking/${stakingConfig.id}`, updateData)
      
      if (response.data.success) {
        onUpdate()
        handleClose()
      }
    } catch (error) {
      console.error('Failed to update staking config:', error)
      setError(error.response?.data?.message || '스테이킹 설정 수정에 실패했습니다.')
    } finally {
      setLoading(false)
    }
  }

  const handleClose = () => {
    setFormData({
      currency: 'krw',
      dailyAmount: '',
      description: '',
      isActive: true
    })
    setError('')
    onClose()
  }

  if (!isOpen || !stakingConfig) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold text-gray-900">
            스테이킹 설정 수정
          </h3>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        {/* User Info */}
        <div className="mb-4 p-3 bg-gray-50 rounded-lg">
          <h4 className="text-sm font-medium text-gray-700 mb-1">대상 사용자</h4>
          <p className="text-sm text-gray-900">
            {stakingConfig.user?.name} (@{stakingConfig.user?.username})
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              통화 종류
            </label>
            <select
              name="currency"
              value={formData.currency}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            >
              <option value="krw">원화 (KRW)</option>
              <option value="token">토큰 (TOKEN)</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              일일 지급량
            </label>
            <input
              type="number"
              name="dailyAmount"
              value={formData.dailyAmount}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="예: 1000"
              min="0"
              step="0.01"
              required
            />
            <p className="mt-1 text-xs text-gray-500">
              매일 지급될 {formData.currency === 'krw' ? '원화' : '토큰'} 금액
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              설명 (선택사항)
            </label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
              placeholder="스테이킹 설정에 대한 설명을 입력하세요"
              rows={3}
              maxLength={500}
            />
            <p className="mt-1 text-xs text-gray-500">
              {formData.description.length}/500
            </p>
          </div>

          <div className="flex items-center">
            <input
              type="checkbox"
              name="isActive"
              checked={formData.isActive}
              onChange={handleChange}
              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
            />
            <label className="ml-2 block text-sm text-gray-700">
              활성 상태
            </label>
          </div>

          {error && (
            <div className="text-red-600 text-sm bg-red-50 p-3 rounded-md">
              {error}
            </div>
          )}

          <div className="flex gap-3 pt-4">
            <button
              type="submit"
              disabled={loading}
              className="flex-1 bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? '수정 중...' : '수정'}
            </button>
            <button
              type="button"
              onClick={handleClose}
              className="flex-1 px-4 py-2 text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300"
            >
              취소
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}